import React, { Component, useEffect, useLayoutEffect, useRef } from "react";
import * as UM from "uglymol";
import shortid from "shortid";
import { memoize } from "./utils";
import { ViewerContext } from "./MolstackContexts";
import * as Comlink from "comlink";

const UMWorker = Comlink.wrap(
  new Worker("./UglymolViewerWorker.js", { type: "module" })
);

const prepareMap = memoize(
  async function _prepareMap(data, key) {
    const result = await UMWorker.prepareMap(data);
    // Recrate
    const grid = new UM.GridArray(result.grid_dim);
    grid.values = result.grid_values;

    const unit_cell = new UM.UnitCell(...result.unit_cell);

    const elmap = new UM.ElMap();

    elmap.unit_cell = unit_cell;
    elmap.grid = grid;
    elmap.stats = result.stats;

    return elmap;
  },
  (data, key) => key
);

const prepareModel = memoize(
  async function _prepareModel(data, key) {
    return UM.modelsFromPDB(data);
  },
  (data, key) => key
);

export default class UglymolViewer {
  constructor(options = {}) {
    this.id = "st-viewer-" + shortid.generate();
    this.Component = this.Component.bind(this);
    this.loadModel = this.loadModel.bind(this);
    this.loadMap = this.loadMap.bind(this);
    this.removeModel = this.removeModel.bind(this);
    this.removeMap = this.removeMap.bind(this);
    this.setModelProperty = this.setModelProperty.bind(this);
    this.setMapProperty = this.setMapProperty.bind(this);
    this.synchronize = this.synchronize.bind(this);

    this._viewer = null;
    this.tied = options.tied || [];
    this._models = {};
    this._maps = {};
    this._default_zoom = 1;
  }

  clearViewer() {
    for (let map of this._viewer.map_bags) {
      this._viewer.clear_el_objects(map);
    }
    for (let model of this._viewer.model_bags) {
      this._viewer.clear_atomic_objects(model);
    }
    this._viewer.map_bags = [];
    this._viewer.model_bags = [];
  }
  // componentDidUpdate(prevProps) {
  //   let change = false;
  //   //FIXME: do we have to check viewer?
  //   // let [old_elements, new_elements, obsolete_elements] =
  //   //     oldNewPartition(this.props.elements, prevProps.elements);
  //
  //   if (!sameElements(this.props.elements, prevProps.elements)) {
  //     old_elements, new_elements = partitionOldNewElements(this.props.elements, prevPro)
  //     this.refreshElements();
  //   }
  //
  //   if (this._viewer &&
  //     ((this.props.width !== prevProps.width) ||
  //     (this.props.height !== prevProps.height))) {
  //     this._viewer.resize();
  //   }
  //
  //   if (change) {
  //     this._viewer.redraw_models();
  //   }
  // }

  synchronize() {
    const this_viewer = this._viewer;
    for (let t of this.tied) {
      const v = t._viewer;
      v.camera.copy(this_viewer.camera);
      v.target.copy(this_viewer.target);
    }
  }

  getView() {
    return this._viewer ? this._viewer.get_view() : null;
  }

  Component(props) {
    const style = {
      width: "100%", //props.width,
      height: "100%", //props.height,
      position: "absolute",
      top: 0,
      left: 0
    };

    useEffect(() => {
      if (this._viewer) {
        //TODO: move remount to uglymol
        this._viewer.container = document.getElementById(this.id);
        document
          .getElementById(this.id)
          .appendChild(this._viewer.renderer.domElement);
      } else {
        const viewer = new UM.Viewer({
          viewer: this.id,
          focusable: true
        });

        //Remove listeners created by UM
        viewer.remove_event_handler("resize");
        viewer.remove_event_handler("mousewheel");
        viewer.remove_event_handler("MozMousePixelScroll");

        this._viewer = viewer;
        // Tap into view chainging methods
        const update_camera_tied = f => (...args) => {
          f(...args);
          this.synchronize();
        };

        const invoke_tied = name => (...args) => {
          this._viewer[name + "_untied"](...args);
          for (let t of this.tied) {
            if (t.tied.some(v => v == this)) {
              //TODO: find better way
              const rr = t._viewer.request_render;
              const r = t._viewer.render;
              t._viewer.request_render = t._viewer.request_render_untied;
              t._viewer.render = t._viewer.render_untied;
              t._viewer[name]();
              t._viewer.request_render = rr;
              t._viewer.render = r;
              // t._viewer[name + '_untied'] && t._viewer[name + '_untied']()
            } else {
              t._viewer[name]();
            }
          }
        };

        this._viewer.update_camera_untied = this._viewer.update_camera.bind(
          this._viewer
        );
        this._viewer.update_camera = update_camera_tied(
          this._viewer.update_camera_untied
        );

        this._viewer.request_render_untied = this._viewer.request_render.bind(
          this._viewer
        );
        this._viewer.request_render = invoke_tied("request_render");

        this._viewer.render_untied = this._viewer.render.bind(this._viewer);
        this._viewer.render = invoke_tied("render");
      }
    }, []); //TODO: cleanup

    useLayoutEffect(() => {
      this._viewer && this._viewer.resize();
    }, [props.width, props.height]);
    useLayoutEffect(() => {
      if (this._viewer && props.view && props.view.origin !== this.id) {
        if (props.view.mode === "reset")
          this._viewer.set_view({
            view: { zoom: this._default_zoom },
            redraw: true
          });
        else {
          this._viewer.set_view({ view: props.view, redraw: true });
          this._viewer.request_render();
        }
      }
    }, [props.view]);

    return (
      <ViewerContext.Provider value={this}>
        <div id={this.id} className={"viewer"} style={style}></div>
        <div>{props.children}</div>
      </ViewerContext.Provider>
    );
  }

  prepareModel = prepareModel;

  async loadModel(models, key) {
    // models is iterable, can have multiple models
    if (key in this._models) {
      console.warn("Model " + key + " already exists. Removing");
      this.removeModel(key);
    }
    const _models = [];
    for (let model of models) {
      const idx = this._viewer.model_bags.length;
      this._viewer.add_model(model);
      _models.push(this._viewer.model_bags[idx]);
    }
    //Store default zoom after loading first model
    if (_models.length === 1) this._default_zoom = this._viewer.camera.zoom;
    this._models[key] = _models;
  }

  removeModel(key) {
    if (key in this._models) {
      const _models = this._models[key];
      this._viewer.model_bags = this._viewer.model_bags.filter(
        m => !_models.includes(m)
      );
      for (let model of _models) {
        this._viewer.clear_atomic_objects(model);
      }
      delete this._models[key];
      this._viewer.request_render();
    }
  }

  setModelProperty(key, name, value) {} //None yet

  prepareMap = prepareMap;

  async loadMap(obj, key) {
    if (key in this._maps) {
      console.warn("Map " + key + " already exists. Removing");
      this.removeMap(key);
    }
    const idx = this._viewer.map_bags.length;
    this._viewer.add_map(obj);
    this._maps[key] = [this._viewer.map_bags[idx]];
  }

  removeMap(key) {
    if (key in this._maps) {
      const _maps = this._maps[key];
      this._viewer.map_bags = this._viewer.map_bags.filter(
        m => !_maps.includes(m)
      );
      for (let map of _maps) {
        this._viewer.clear_el_objects(map);
      }
      delete this._maps[key];
      this._viewer.request_render();
    }
  }

  setMapProperty(key, name, value) {
    const _maps = this._maps[key];
    if (!_maps) return;

    for (let m of _maps) {
      switch (name) {
        case "color":
          m.color = value ? value : "#004DCF";
          break;
        case "rmsd":
          m.isolevel = value ? value : 1;
          break;
        case "difference":
          if (value) {
            m.types = ["map_neg", "map_pos"];
          } else {
            m.types = ["map_den"];
          }
          break;
        case "hidden":
          m.visible = value ? false : true;
          break;
      }
      this._viewer.redraw_map(m);
      this._viewer.request_render();
    }
  }

  errorOrStatus(state) {
    return state.error ? state.error.toString() : state.status || "...";
  }

  renderModelDOM(props) {
    return "";
  }

  renderMapDOM(props) {
    return "";
  }
}
